import {
  fiberQuestionsSchema,
  isProClientType,
  packCodeSchema,
  projectInfoSchema,
  projectNatureLabels,
  projectNatureSchema,
  proPrestationLabels,
  proPrestationSchema,
  unitTypeSchema,
  unitTypologySchema,
  type FiberQuestions,
  type PackCode,
  type Project,
  type ProjectInfo,
  type ProPrestation,
  type RequestType,
  type UnitType,
  type UnitTypology,
  getQualificationConfig,
  requestTypeSchema,
} from '@bcl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { AddressAutocomplete } from '@/components/address-autocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { extractErrorMessage } from '@/features/auth/error-message';
import { DocumentsSection } from '@/features/documents/documents-section';
import { listDocumentsForProject } from '@/features/documents/documents-api';
import { PaymentSection } from '@/features/payments/payment-section';
import { listPaymentsForProject } from '@/features/payments/payments-api';
import { useAuth } from '@/lib/auth-context';
import { updateProject } from './projects-api';
import { requestTypeLabels } from './labels';

const proSelectClasses =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm';
import { listActivePacks } from './packs-public-api';
import { resolveInitialStep, type WizardStepKey } from './booking-progress';

type StepKey = WizardStepKey;

export function ProjectWizard({
  project,
  onUpdated,
  onCompleted,
}: {
  project: Project;
  onUpdated: (p: Project) => void;
  onCompleted?: (p: Project) => void;
}) {
  // L'etape courante est derivee du projet : on prend le premier champ non
  // rempli (pack -> qualif -> info -> fiber -> documents -> payment). On
  // permet aussi un override manuel via setManualStep pour les boutons
  // « Retour » et « Suivant » qui anticipent l'etat asynchrone.
  const documents = useQuery({
    queryKey: ['documents', 'by-project', project.id],
    queryFn: () => listDocumentsForProject(project.id),
  });
  const payments = useQuery({
    queryKey: ['payments', 'by-project', project.id],
    queryFn: () => listPaymentsForProject(project.id),
  });
  const documentsCount = documents.data?.length ?? 0;
  const paymentDone = (payments.data ?? []).some((p) => p.status === 'paye');

  const [manualStep, setManualStep] = useState<StepKey | null>(null);
  const step: StepKey =
    manualStep ?? resolveInitialStep(project, { documentsCount, paymentDone });
  const setStep = setManualStep;
  const [serverError, setServerError] = useState<string | null>(null);

  async function patch(payload: Parameters<typeof updateProject>[1]) {
    try {
      setServerError(null);
      const next = await updateProject(project.id, payload);
      // Reset l'override manuel pour que le step se re-derive du projet.
      setManualStep(null);
      onUpdated(next);
      return next;
    } catch (err) {
      setServerError(extractErrorMessage(err));
      throw err;
    }
  }

  return (
    <div className="space-y-6">
      {serverError && (
        <p role="alert" className="text-sm text-destructive">
          {serverError}
        </p>
      )}

      {step === 'pack' && (
        <PackStep
          requestType={project.requestType}
          onSubmit={async (packCode) => {
            try {
              await patch({ packCode });
              setStep('qualification');
            } catch (err) {
              setServerError(extractErrorMessage(err));
            }
          }}
        />
      )}
      {step === 'qualification' && (
        <QualificationStep
          initialRequestType={project.requestType}
          onSubmit={async (requestType, answers) => {
            await patch({ requestType, qualificationAnswers: answers });
            setStep('info');
          }}
          onBack={() => setStep('pack')}
        />
      )}
      {step === 'info' && (
        <InfoStep
          onSubmit={async (info, unitTypologies) => {
            await patch({ info, unitTypologies });
            setStep('fiber');
          }}
          onBack={() => setStep('qualification')}
        />
      )}
      {step === 'fiber' && (
        <FiberStep
          onSubmit={async (q) => {
            await patch({ fiberQuestions: q });
            setStep('documents');
          }}
          onBack={() => setStep('info')}
        />
      )}
      {step === 'documents' && (
        <DocumentsStep
          project={project}
          onContinue={() => setStep('payment')}
          onBack={() => setStep('fiber')}
        />
      )}
      {step === 'payment' && (
        <PaymentStep
          project={project}
          documentsCount={documentsCount}
          onBack={() => setStep('documents')}
        />
      )}
      {step === 'done' && (
        <DoneStep onSubmit={() => onCompleted?.(project)} />
      )}
    </div>
  );
}

function QualificationStep({
  initialRequestType,
  onSubmit,
  onBack,
}: {
  initialRequestType: RequestType;
  onSubmit: (
    requestType: RequestType,
    answers: Record<string, string | boolean | number>,
  ) => Promise<void>;
  onBack: () => void;
}) {
  const [requestType, setRequestType] = useState<RequestType>(initialRequestType);
  const cfg = getQualificationConfig(requestType);
  const form = useForm<Record<string, string | boolean | number>>({
    defaultValues: Object.fromEntries(
      cfg.fields.map((f) => [
        f.id,
        f.type === 'boolean' ? false : f.type === 'number' ? 0 : '',
      ]),
    ),
  });

  return (
    <form
      onSubmit={form.handleSubmit((answers) => onSubmit(requestType, answers))}
      className="space-y-4"
    >
      <h2 className="text-xl font-semibold">Étape 2 — Qualifier votre demande</h2>
      <div className="space-y-1.5">
        <Label htmlFor="requestType">Votre demande concerne</Label>
        <select
          id="requestType"
          value={requestType}
          onChange={(e) => {
            const parsed = requestTypeSchema.safeParse(e.target.value);
            if (parsed.success) {
              setRequestType(parsed.data);
              form.reset(
                Object.fromEntries(
                  getQualificationConfig(parsed.data).fields.map((f) => [
                    f.id,
                    f.type === 'boolean' ? false : f.type === 'number' ? 0 : '',
                  ]),
                ),
              );
            }
          }}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          {(Object.keys(requestTypeLabels) as RequestType[]).map((k) => (
            <option key={k} value={k}>
              {requestTypeLabels[k]}
            </option>
          ))}
        </select>
      </div>
      <p className="text-sm text-muted-foreground">{cfg.description}</p>
      {cfg.fields.map((f) => (
        <div key={f.id} className="space-y-1.5">
          <Label htmlFor={f.id}>{f.label}</Label>
          {f.type === 'boolean' ? (
            <input
              id={f.id}
              type="checkbox"
              {...form.register(f.id)}
              className="h-4 w-4"
            />
          ) : f.type === 'select' ? (
            <select
              id={f.id}
              {...form.register(f.id)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <Input
              id={f.id}
              type={f.type === 'number' ? 'number' : 'text'}
              {...form.register(f.id, {
                valueAsNumber: f.type === 'number',
              })}
            />
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onBack}>
          Retour
        </Button>
        <Button type="submit">Suivant</Button>
      </div>
    </form>
  );
}

const UNIT_TYPE_LABELS: Record<UnitType, string> = {
  t1: 'T1',
  t2: 'T2',
  t3: 'T3',
  t4: 'T4',
  maison: 'Maison',
  bureau: 'Bureau',
  commerce: 'Commerce',
  entrepot: 'Entrepôt',
};

const UNIT_TYPE_OPTIONS = unitTypeSchema.options;

const infoFormSchema = projectInfoSchema.extend({
  unitTypologies: z.array(unitTypologySchema),
});
type InfoFormValues = z.infer<typeof infoFormSchema>;

function InfoStep({
  onSubmit,
  onBack,
}: {
  onSubmit: (info: ProjectInfo, unitTypologies: UnitTypology[]) => Promise<void>;
  onBack: () => void;
}) {
  const { user } = useAuth();
  const isPro = isProClientType(user?.clientType);
  const form = useForm<InfoFormValues>({
    resolver: zodResolver(infoFormSchema),
    defaultValues: {
      address: { line1: '', city: '', postalCode: '', country: 'France' },
      contactName: '',
      contactPhone: '',
      desiredDelay: '',
      buildingState: 'neuf',
      buildingsCount: 1,
      housingCount: 0,
      housingPerBuilding: 0,
      fiberInstallationsCount: 0,
      proLocalsCount: 0,
      unitTypologies: [],
    },
  });

  const { fields, append, remove } = useFieldArray<InfoFormValues, 'unitTypologies'>({
    control: form.control,
    name: 'unitTypologies',
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    const { unitTypologies, ...info } = values;
    await onSubmit(info, unitTypologies);
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-xl font-semibold">Étape 3 — Informations projet</h2>

      <div className="space-y-1.5">
        <Label htmlFor="line1">Adresse</Label>
        <AddressAutocomplete
          id="line1"
          placeholder="Commencez à saisir votre adresse…"
          value={form.watch('address.line1') ?? ''}
          onValueChange={(v) =>
            form.setValue('address.line1', v, { shouldValidate: true })
          }
          onSelect={(sel) => {
            form.setValue('address.line1', sel.address, {
              shouldValidate: true,
            });
            if (sel.postalCode) {
              form.setValue('address.postalCode', sel.postalCode, {
                shouldValidate: true,
              });
            }
            if (sel.city) {
              form.setValue('address.city', sel.city, {
                shouldValidate: true,
              });
            }
          }}
        />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="city">Ville</Label>
          <Input id="city" {...form.register('address.city')} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="postalCode">Code postal</Label>
          <Input id="postalCode" {...form.register('address.postalCode')} />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="contactName">Nom et prénom du contact sur place</Label>
          <Input
            id="contactName"
            placeholder="Ex: Jean Dupont"
            {...form.register('contactName')}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="contactPhone">Téléphone du contact sur place</Label>
          <Input
            id="contactPhone"
            type="tel"
            placeholder="Ex: 06 12 34 56 78"
            {...form.register('contactPhone')}
          />
        </div>
      </div>

      {isPro && (
        <div className="space-y-4 rounded-md border border-dashed p-4">
          <h3 className="text-sm font-semibold text-bcl-blue">
            Professionnels &amp; collectivités
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="organisation">Société / Organisme</Label>
              <Input id="organisation" {...form.register('organisation')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contactFunction">Fonction du contact</Label>
              <Input id="contactFunction" {...form.register('contactFunction')} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="projectNature">Nature du projet</Label>
            <select
              id="projectNature"
              className={proSelectClasses}
              {...form.register('projectNature', {
                setValueAs: (v) => (v ? v : undefined),
              })}
            >
              <option value="">— Choisir —</option>
              {projectNatureSchema.options.map((value) => (
                <option key={value} value={value}>
                  {projectNatureLabels[value]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Prestations demandées</Label>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {proPrestationSchema.options.map((value) => {
                const current = (form.watch('prestations') ?? []) as ProPrestation[];
                const checked = current.includes(value);
                return (
                  <label key={value} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        form.setValue(
                          'prestations',
                          e.target.checked
                            ? [...current, value]
                            : current.filter((p) => p !== value),
                        )
                      }
                    />
                    {proPrestationLabels[value]}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="desiredDelay">Délai souhaité</Label>
          <select
            id="desiredDelay"
            {...form.register('desiredDelay')}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Sélectionner un délai…</option>
            <option value="urgent_moins_1_mois">Sous 1 mois (urgent)</option>
            <option value="1_a_3_mois">1 à 3 mois</option>
            <option value="3_a_6_mois">3 à 6 mois</option>
            <option value="6_a_12_mois">6 à 12 mois</option>
            <option value="plus_12_mois">Plus de 12 mois</option>
            <option value="non_defini">Pas encore défini</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="buildingState">État</Label>
          <select
            id="buildingState"
            {...form.register('buildingState')}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="neuf">Neuf</option>
            <option value="renovation">Rénovation</option>
          </select>
        </div>
      </div>

      <fieldset className="space-y-3 rounded-md border p-4">
        <legend className="px-1 text-sm font-medium">Données techniques</legend>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="buildingsCount">Nombre de bâtiments</Label>
            <Input
              id="buildingsCount"
              type="number"
              min={1}
              {...form.register('buildingsCount', { valueAsNumber: true })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="housingCount">Nombre total de logements</Label>
            <Input
              id="housingCount"
              type="number"
              min={0}
              {...form.register('housingCount', { valueAsNumber: true })}
            />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="housingPerBuilding">Logements par bâtiment</Label>
            <Input
              id="housingPerBuilding"
              type="number"
              min={0}
              {...form.register('housingPerBuilding', { valueAsNumber: true })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fiberInstallationsCount">
              Nombre d’installations fibre
            </Label>
            <Input
              id="fiberInstallationsCount"
              type="number"
              min={0}
              {...form.register('fiberInstallationsCount', { valueAsNumber: true })}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="proLocalsCount">Nombre de locaux professionnels</Label>
          <Input
            id="proLocalsCount"
            type="number"
            min={0}
            {...form.register('proLocalsCount', { valueAsNumber: true })}
          />
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded-md border p-4">
        <legend className="px-1 text-sm font-medium">Typologie des unités</legend>
        {fields.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Aucune typologie renseignée. Ajoutez les types d’unités présents sur le
            projet.
          </p>
        )}
        <ul className="space-y-2">
          {fields.map((field, index) => (
            <li key={field.id} className="grid gap-2 md:grid-cols-[1fr_140px_auto]">
              <div className="space-y-1.5">
                <Label htmlFor={`unitTypologies-${index}-type`}>Type</Label>
                <select
                  id={`unitTypologies-${index}-type`}
                  aria-label={`Type d’unité ${index + 1}`}
                  {...form.register(`unitTypologies.${index}.unitType` as const)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {UNIT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {UNIT_TYPE_LABELS[opt]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`unitTypologies-${index}-count`}>Quantité</Label>
                <Input
                  id={`unitTypologies-${index}-count`}
                  type="number"
                  min={0}
                  aria-label={`Quantité unité ${index + 1}`}
                  {...form.register(`unitTypologies.${index}.count` as const, {
                    valueAsNumber: true,
                  })}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => remove(index)}
                  aria-label={`Supprimer la typologie ${index + 1}`}
                >
                  Supprimer
                </Button>
              </div>
            </li>
          ))}
        </ul>
        <Button
          type="button"
          variant="outline"
          onClick={() => append({ unitType: 't1', count: 0 })}
        >
          Ajouter une typologie
        </Button>
      </fieldset>

      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onBack}>
          Retour
        </Button>
        <Button type="submit">Suivant</Button>
      </div>
    </form>
  );
}

function FiberStep({
  onSubmit,
  onBack,
}: {
  onSubmit: (q: FiberQuestions) => Promise<void>;
  onBack: () => void;
}) {
  const form = useForm<FiberQuestions>({
    resolver: zodResolver(fiberQuestionsSchema),
    defaultValues: {
      fiberKnownAtAddress: false,
      fiberAlreadyInstalled: false,
      hasFourreaux: false,
      hasTelecomChamber: false,
      connectionType: 'inconnu',
      civilWorksDone: false,
    },
  });

  const checks: { name: keyof FiberQuestions; label: string }[] = [
    { name: 'fiberKnownAtAddress', label: 'Fibre connue à l’adresse ?' },
    { name: 'fiberAlreadyInstalled', label: 'Fibre déjà installée ?' },
    { name: 'hasFourreaux', label: 'Présence de fourreaux ?' },
    { name: 'hasTelecomChamber', label: 'Présence d’une chambre télécom ?' },
    { name: 'civilWorksDone', label: 'Génie civil déjà réalisé ?' },
  ];

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <h2 className="text-xl font-semibold">Étape 4 — Questions fibre</h2>
      {checks.map((c) => (
        <label key={c.name} className="flex items-center gap-2 text-sm">
          <input type="checkbox" {...form.register(c.name)} className="h-4 w-4" />
          {c.label}
        </label>
      ))}
      <div className="space-y-1.5">
        <Label htmlFor="fiberInFrontOfProperty">
          La fibre est-elle présente devant la propriété ?
        </Label>
        <select
          id="fiberInFrontOfProperty"
          {...form.register('fiberInFrontOfProperty', {
            setValueAs: (v) => (v ? v : undefined),
          })}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">— Sans réponse —</option>
          <option value="oui">Oui</option>
          <option value="non">Non</option>
          <option value="je_ne_sais_pas">Je ne sais pas</option>
        </select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="connectionType">Type de raccordement</Label>
        <select
          id="connectionType"
          {...form.register('connectionType')}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="inconnu">Inconnu</option>
          <option value="aerien">Aérien</option>
          <option value="souterrain">Souterrain</option>
        </select>
      </div>

      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onBack}>
          Retour
        </Button>
        <Button type="submit">Suivant</Button>
      </div>
    </form>
  );
}

function formatPackPriceRange(min: number, max: number): string {
  if (min === max) return `${min} € HT`;
  return `${min} € — ${max} € HT`;
}

function PackStep({
  requestType,
  onSubmit,
}: {
  requestType: RequestType;
  onSubmit: (packCode: PackCode) => Promise<void>;
}) {
  const packs = useQuery({
    queryKey: ['public', 'packs'],
    queryFn: listActivePacks,
  });
  // La qualification n'a pas encore eu lieu : on affiche TOUS les packs.
  // Si le projet porte deja un requestType (cree depuis /projects/new avec
  // valeur par defaut etude_raccordement), on met en avant les packs
  // suggeres pour ce type, mais on ne filtre pas.
  const cfg = getQualificationConfig(requestType);
  const suggested = new Set<PackCode>(cfg.suggestedPacks);

  const items =
    packs.data && packs.data.length > 0
      ? packs.data.map((p) => ({
          code: p.code,
          name: p.name,
          description: p.description,
          priceLabel: formatPackPriceRange(p.priceMinHt, p.priceMaxHt),
        }))
      : [];

  const defaultPack: PackCode | undefined =
    items.find((p) => suggested.has(p.code))?.code ?? items[0]?.code;

  // Schema attendu par useForm : un object `{packCode}`. L'ancienne version
  // utilisait `packCodeSchema.transform(v => ({packCode: v}))` qui attendait
  // une chaine en entree alors que useForm fournit deja un object, donc le
  // zodResolver echouait silencieusement la validation et handleSubmit
  // n'appelait jamais onSubmit (le bouton "Valider" semblait inerte).
  const form = useForm<{ packCode: PackCode }>({
    resolver: zodResolver(z.object({ packCode: packCodeSchema })),
    defaultValues: { packCode: 'serenite_fibre' },
  });

  useEffect(() => {
    if (defaultPack && !form.formState.isDirty) {
      form.reset({ packCode: defaultPack });
    }
  }, [defaultPack, form]);

  return (
    <form
      onSubmit={form.handleSubmit((v) => onSubmit(v.packCode))}
      className="space-y-4"
    >
      <h2 className="text-xl font-semibold">Étape 1 — Choisir votre pack</h2>
      <p className="text-sm text-muted-foreground">
        Le funnel BCL commence toujours par le choix du pack. Vous pourrez
        ensuite qualifier votre demande, renseigner votre projet et déposer vos
        documents.
      </p>
      {packs.isLoading && (
        <p className="text-sm text-muted-foreground">Packs en cours de chargement…</p>
      )}
      {!packs.isLoading && items.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Aucun pack disponible pour le moment. Merci de revenir plus tard.
        </p>
      )}
      {items.map((p) => (
        <label
          key={p.code}
          className="flex items-start gap-3 rounded-md border p-3"
        >
          <input
            type="radio"
            value={p.code}
            {...form.register('packCode')}
            className="mt-1"
          />
          <span className="text-sm flex-1">
            <span className="block font-medium">{p.name}</span>
            {p.description && (
              <span className="block text-muted-foreground">{p.description}</span>
            )}
            {p.priceLabel && (
              <span className="mt-1 block text-bcl-blue">{p.priceLabel}</span>
            )}
          </span>
        </label>
      ))}
      <div className="flex gap-2">
        <Button type="submit" disabled={items.length === 0}>
          Valider mon pack et continuer
        </Button>
      </div>
    </form>
  );
}

function DocumentsStep({
  project,
  onContinue,
  onBack,
}: {
  project: Project;
  onContinue: () => void;
  onBack: () => void;
}) {
  const documents = useQuery({
    queryKey: ['documents', project.id],
    queryFn: () => listDocumentsForProject(project.id),
  });
  const [docsBusy, setDocsBusy] = useState(false);
  const requiredTypes = getQualificationConfig(project.requestType).requiredDocuments;
  const uploadedTypes = new Set((documents.data ?? []).map((d) => d.documentType));
  const allRequiredCovered = requiredTypes.every((t) => uploadedTypes.has(t));

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Étape 5 — Déposer mes documents</h2>
      <p className="text-sm text-muted-foreground">
        Les documents requis dépendent du pack choisi et de votre qualification.
      </p>
      <DocumentsSection
        projectId={project.id}
        requestType={project.requestType}
        onBusyChange={setDocsBusy}
      />
      {docsBusy && (
        <p className="text-sm text-bcl-blue">
          Téléversement en cours… patientez avant de passer au paiement.
        </p>
      )}
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onBack}>
          Retour
        </Button>
        <Button
          type="button"
          onClick={onContinue}
          disabled={!allRequiredCovered || docsBusy}
        >
          Passer au paiement
        </Button>
      </div>
    </div>
  );
}

function PaymentStep({
  project,
  documentsCount,
  onBack,
}: {
  project: Project;
  documentsCount: number;
  onBack: () => void;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Étape 6 — Régler mon dossier</h2>
      <p className="text-sm text-muted-foreground">
        Le paiement vaut soumission automatique de votre dossier à BCL Telecom.
      </p>
      <PaymentSection project={project} documentsCount={documentsCount} />
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onBack}>
          Retour
        </Button>
      </div>
    </div>
  );
}

function DoneStep({ onSubmit }: { onSubmit: () => void }) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Dossier complet</h2>
      <p className="text-sm text-muted-foreground">
        Toutes les étapes sont validées et votre paiement a été enregistré.
      </p>
      <Button onClick={onSubmit}>Voir le suivi de mon dossier</Button>
    </div>
  );
}
